import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import AdminDashboard from "@/pages/AdminDashboard";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import Projects from "@/pages/Projects";
import TimeTracking from "@/pages/TimeTracking";
import DailyReports from "@/pages/DailyReports";
import Leaves from "@/pages/Leaves";
import Uploads from "@/pages/Uploads";
import Documents from "@/pages/Documents";
import Users from "@/pages/Users";
import AuditLog from "@/pages/AuditLog";
import Exports from "@/pages/Exports";
import Calendar from "@/pages/Calendar";
import Profile from "@/pages/Profile";
import ChangePassword from "@/pages/ChangePassword";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

function Protected({ children, admin = false }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading || user === null) {
    return (
      <div className="h-screen w-screen grid place-items-center bg-black text-white font-mono">
        <span className="label-eyebrow">LOADING SYSTEM…</span>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Force first-login password change for everyone except on the change-password page itself
  if (user.must_change_password && loc.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function RoleHome() {
  const { user } = useAuth();
  return user?.role === "admin" ? <AdminDashboard /> : <EmployeeDashboard />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/change-password"
            element={
              <Protected>
                <ChangePassword />
              </Protected>
            }
          />
          <Route
            element={
              <Protected>
                <Layout />
              </Protected>
            }
          >
            <Route path="/" element={<RoleHome />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/time" element={<TimeTracking />} />
            <Route path="/daily-reports" element={<DailyReports />} />
            <Route path="/leaves" element={<Leaves />} />
            <Route path="/uploads" element={<Uploads />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/users" element={<Protected admin><Users /></Protected>} />
            <Route path="/audit" element={<Protected admin><AuditLog /></Protected>} />
            <Route path="/exports" element={<Protected admin><Exports /></Protected>} />
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/profile" element={<Profile />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster theme="dark" position="top-right" richColors />
    </AuthProvider>
  );
}
